import {Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import {TriggerQueryAction} from '@models/adminstration/machine/trigger/trigger-query/trigger-query-action';
import {TriggerQueryService} from '@services/trigger/triger-action/trigger-query.service';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@services/notification/notification.service';
import {MessageContentType} from '@models/adminstration/machine/trigger/trigger-query/message-content-type';
import {startWith, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {ValidatorsService} from '@services/validators/validators.service';
import {ARGOS_EVENT_FIELDS} from '@constants/administration/machine/pubsub-protobuf-fields/pubsub-protobuf-fields';
import {TRIGGER_SIGNAL_TYPE} from '@constants/administration/machine/trigger-signal-label';
import {TriggerHelperService} from '@services/trigger/helper/trigger-helper.service';
import {
  AdditionalMessageField, AdditionalMessageFieldTypes
} from '@models/adminstration/machine/trigger/trigger-query/additional-message-field/additional-message-field';

type ChangeCallbackFn<T> = (value: T) => void;
type TouchCallbackFn = () => void;

@Component({
    selector: 'scitis-admin-pub-sub-action-type',
    templateUrl: './pub-sub-action-type.component.html',
    styleUrls: ['./pub-sub-action-type.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PubSubActionTypeComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => PubSubActionTypeComponent),
            multi: true
        }
    ],
    standalone: false
})
export class PubSubActionTypeComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy {

  @Input()
  triggerQueryAction: TriggerQueryAction = new TriggerQueryAction();

  @Output()
  updatedTriggerQueryAction: EventEmitter<TriggerQueryAction> = new EventEmitter<TriggerQueryAction>();

  @Output()
  formIsValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  isLoadingTopics: boolean = false;
  topics: string[] = [];
  triggerQueryPubSubForm: UntypedFormGroup = this.fb.group({});
  messageContentTypeItems: string[] = Object.keys(MessageContentType);
  messageContentType = MessageContentType;
  signalLabels: string[] = Object.keys(TRIGGER_SIGNAL_TYPE);
  private CURRENT_PAGE_NAME: string = 'PubSubActionTypeComponent';
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private fb: UntypedFormBuilder,
    private triggerQueryService: TriggerQueryService,
    private translate: TranslateService,
    private notificationService: NotificationService,
    private validatorsService: ValidatorsService,
    private triggerHelper: TriggerHelperService,
  ) {
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.triggerQueryPubSubForm.controls;
  }

  get argosEventFields(): UntypedFormGroup[] {
    return this.triggerQueryPubSubForm.controls.argosEvent['controls'];
  }

  ngOnInit(): void {
    this.generateTriggerQueryPubSubForm();
    this.setPubSubTopics();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.triggerQueryPubSubForm.invalid) {
      return {invalidForm: {valid: false, message: 'PubSubActionTypeComponent fields are invalid'}};
    }
    return null;
  }

  writeValue(val: any): void {
    if (val) {
      this.triggerQueryPubSubForm.setValue(val, {emitEvent: false});
    }
  }

  registerOnChange(fn: ChangeCallbackFn<object>): void {
    this.triggerQueryPubSubForm.valueChanges.subscribe(fn);

  }

  registerOnTouched(fn: TouchCallbackFn): void {
    // needed to implement ControlValueAccessor interface
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.triggerQueryPubSubForm.disable();
    } else {
      this.triggerQueryPubSubForm.enable();
    }
  }

  onFocusOut(): void {
    this.onTriggerQueryPubSubUpdated();
  }

  onChangeType($event: MessageContentType) {
    this.triggerQueryAction.messageContentType = $event;
  }

  private onTriggerQueryPubSubUpdated() {
    const triggerQueryActionUpdated = new TriggerQueryAction({
      topic: this.f.topic.value,
      messageContentType: this.f.messageContentType.value,
    } as TriggerQueryAction);
    switch (this.f.messageContentType.value) {
      case MessageContentType.TEXT:
        triggerQueryActionUpdated.message = this.f.text.value;
        break;
      case MessageContentType.JSON:
        triggerQueryActionUpdated.message = this.f.JSON.value;
        break;
      case MessageContentType.ARGOS_EVENT:
        triggerQueryActionUpdated.additionalMessageFields = this.f.argosEvent.value;
        break;
    }

    this.updatedTriggerQueryAction.emit(triggerQueryActionUpdated);
  }

  private generateTriggerQueryPubSubForm(): void {
    const textControlValue = this.triggerQueryAction.messageContentType === MessageContentType.TEXT ? this.triggerQueryAction.message : '';
    const textControl = new UntypedFormControl(textControlValue);
    const JSONControlValue = this.triggerQueryAction.messageContentType === MessageContentType.JSON ? this.triggerQueryAction.message : '';
    const JSONControl = new UntypedFormControl(JSONControlValue, [this.validatorsService.JSONFormatValidator()]);
    const argosEventFields = this.fb.array([]);
    let {additionalMessageFields} = this.triggerQueryAction;
    additionalMessageFields = additionalMessageFields?.map((item) => {
      if (item.fieldName === AdditionalMessageFieldTypes.EventSeverity) {
        return new AdditionalMessageField({...item, value: this.triggerHelper.getEventLabelByNumber(+item.value) as string});
      }
      if (item.fieldName === AdditionalMessageFieldTypes.EventCreated) {
        item.value = item.value === 'true';
      }
      return item;
    }) || null;
    ARGOS_EVENT_FIELDS.forEach((field, index) => {
      const fieldForm = this.fb.group({
        fieldName: [field.fieldName],
        value: [
          additionalMessageFields ? additionalMessageFields[index].value : field.value,
          field.dataType === 'eventSeverity' ? [Validators.required] : []
        ],
        dataType: [field.dataType]
      });
      argosEventFields.push(fieldForm);
    });

    this.triggerQueryPubSubForm = this.fb.group({
      topic: [this.triggerQueryAction.topic, Validators.required],
      messageContentType: [this.triggerQueryAction.messageContentType, Validators.required],
    });
    this.addFormSubscribers(textControl, JSONControl, argosEventFields);
    this.formIsValid.emit(this.triggerQueryPubSubForm.valid);
  }

  private setPubSubTopics(): void {
    this.isLoadingTopics = true;
    this.triggerQueryService.getPubSubTopics().subscribe({
      next: (topics: string[]) => {
        this.topics = topics;
        this.isLoadingTopics = false;
      }, error: error => {
        console.log(error);
        this.isLoadingTopics = false;
        const message = this.translate.instant('There was an error getting Pub/Sub topics.');
        this.notificationService.error(message, error, this.CURRENT_PAGE_NAME, true);
      }
    });
  }

  private addFormSubscribers(textControl: UntypedFormControl, JSONControl: UntypedFormControl, argosEventFields: UntypedFormArray): void {
    this.f.messageContentType.valueChanges.pipe(takeUntil(this.destroy$), startWith(this.triggerQueryAction.messageContentType))
      .subscribe((value: MessageContentType) => {
        this.choseContentType(value, textControl, JSONControl, argosEventFields);
      });
    this.triggerQueryPubSubForm.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.formIsValid.emit(this.triggerQueryPubSubForm.valid);
    });
  }

  private choseContentType(value: MessageContentType, textControl: UntypedFormControl, JSONControl: UntypedFormControl, argosEventFields: UntypedFormArray) {
    switch (value) {
      case MessageContentType.TEXT:
      default:
        this.triggerQueryPubSubForm.addControl('text', textControl);
        this.triggerQueryPubSubForm.removeControl('JSON');
        this.triggerQueryPubSubForm.removeControl('argosEvent');
        break;
      case MessageContentType.JSON:
        this.triggerQueryPubSubForm.addControl('JSON', JSONControl);
        this.triggerQueryPubSubForm.removeControl('text');
        this.triggerQueryPubSubForm.removeControl('argosEvent');
        break;
      case MessageContentType.ARGOS_EVENT:
        this.triggerQueryPubSubForm.addControl('argosEvent', argosEventFields);
        this.triggerQueryPubSubForm.removeControl('text');
        this.triggerQueryPubSubForm.removeControl('JSON');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
